import { useState } from 'react';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Link } from 'react-router-dom';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value || '';
    if (honeypot.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9p0f1qqs38sj6jc7h0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('垃圾')) {
          setFormState('success');
        } else {
          setFormError(msg);
          setFormState('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormState('error');
    }
  };

  const testimonials = [
    {
      name: '小陈',
      role: '韩漫爱好者',
      content: '韩漫网的漫画种类真的很全，更新速度也很快，每天都会来看有没有新的章节。下拉式阅读体验也很流畅！',
      rating: 5,
    },
    {
      name: '星星之火',
      role: '资深漫迷',
      content: '画质清晰，排版舒适，最重要的是完全免费！已经推荐给身边所有喜欢韩漫的朋友了，大家都说好用。',
      rating: 5,
    },
    {
      name: '林小鹿',
      role: '大学生',
      content: '《她的秘密花园》太好看了！在这里找到了好多优质的韩漫，分类也很清晰，找漫画特别方便。',
      rating: 5,
    },
    {
      name: '追风少年',
      role: '上班族',
      content: '每天上下班路上刷几话韩漫已经成了习惯，韩漫网的加载速度很快，地铁上也能流畅阅读。',
      rating: 4,
    },
  ];

  return (
    <>
      <ScrollToTop />
      <main className="min-h-screen pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">关于我们</span>
          </nav>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                关于韩漫网
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                韩漫网是一个专注于韩国漫画在线阅读的中文平台，致力于为中文读者提供最优质的韩漫阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className="ri-book-open-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  收录超过150部优质韩国漫画，涵盖浪漫、剧情、悬疑、奇幻等多种类型，满足不同读者的口味。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-smartphone-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">下拉式阅读</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  采用流畅的下拉式阅读模式，无需翻页即可顺畅阅读，在手机和电脑上都能获得最佳体验。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100">
                  <i className="ri-timer-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">每日更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  热门漫画每日更新，让你第一时间追更最新内容，不错过任何精彩剧情。
                </p>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-8 md:p-10">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">我们的故事</h2>
                  <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      韩漫网创立于2025年，由一群热爱韩国漫画的年轻人共同创建。我们发现，尽管韩国漫画在国内有大量粉丝，但中文读者常常面临寻找优质韩漫资源困难、翻译质量参差不齐等问题。
                    </p>
                    <p>
                      因此，我们决定打造一个专注于韩国漫画的中文阅读平台，让更多的中文读者能够轻松地接触到优秀的韩国漫画作品。我们始终坚持<strong>免费、优质、便捷</strong>的服务理念。
                    </p>
                    <p>
                      如今，韩漫网已经成长为国内领先的韩漫阅读平台之一，日均访问用户超过50万，累计为用户提供了超过数千万次的阅读服务。未来，我们将继续努力，为读者带来更多优质韩漫作品。
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-80">
                  <div className="aspect-square rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20Korean%20style%20reading%20nook%20with%20a%20tablet%20displaying%20webtoon%2C%20soft%20natural%20light%2C%20wooden%20furniture%2C%20warm%20neutral%20tones%2C%20comfortable%20atmosphere%2C%20editorial%20photography%20style%2C%20clean%20composition%2C%20minimalist%20aesthetic&width=800&height=800&seq=about-story-01&orientation=squarish"
                      alt="韩漫网品牌故事"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">看看读者们怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题或建议？欢迎随时与我们联系
                </p>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <div className="visually-hidden">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className={`w-full py-2.5 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      formState === 'submitting'
                        ? 'bg-background-300 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-500 hover:bg-primary-600 text-background-50'
                    }`}
                  >
                    {formState === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : '提交留言'}
                  </button>

                  {formState === 'success' && (
                    <div className="p-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-700 text-center">
                      <i className="ri-check-line mr-1"></i>
                      感谢您的留言，我们会尽快与您联系！
                    </div>
                  )}

                  {formState === 'error' && formError && (
                    <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}
                </form>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-50">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-400 mb-0.5">邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">contact@hanman.com</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-accent-50">
                    <i className="ri-customer-service-2-line text-accent-600 text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-400 mb-0.5">客服时间</p>
                  <p className="text-sm font-medium text-foreground-800">每天 9:00 - 21:00</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-secondary-50">
                    <i className="ri-wechat-line text-secondary-600 text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-400 mb-0.5">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">韩漫网官方</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}